import { Stack } from "@certn/cascadia/foundations/grid-layout/Stack";
import {
  Container,
  Layout,
  Loader,
  SectionMessage,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from "@certn/cascadia-react";
import { parseName } from "@certn/utils";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { AppFooterLinks } from "../../components/app-footer/AppFooterLinks";
import { AppHeader } from "../../components/app-header/AppHeader";
import { AppNavigation } from "../../components/app-navigation/AppNavigation";
import { PersonProfileTitleCard } from "../person-profile/components/PersonProfileTitleCard";
import { ActivityLogTab } from "./components/ActivityLogTab";
import { ChecksMonitoredTab } from "./components/ChecksMonitoredTab";
import { MonitoringApplicantActionsDropdown } from "./components/MonitoringApplicantActionsDropdown";
import { getEligibleChecksForSubject } from "./monitoringEligibleChecks";
import { getApplicantDisplayName } from "./types";
import { useCanEnrollInMonitoring } from "./useCanEnrollInMonitoring";
import {
  useEligibleApplicants,
  useMonitoringApplicantDetail,
  useRefreshMonitoringAfterAction,
} from "./useMonitoringApi";

const MONITORING_PATH = "/monitoring";

export function MonitoringApplicantDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { subjectId } = useParams();
  const [tab, setTab] = useState("checks");
  const canEnroll = useCanEnrollInMonitoring();

  const {
    data: rawDetail,
    isLoading,
    error,
  } = useMonitoringApplicantDetail(subjectId);
  const detail = rawDetail;
  const { data: eligibleData } = useEligibleApplicants(
    subjectId ? { subject_id: subjectId } : null
  );
  const refreshAfterAction = useRefreshMonitoringAfterAction(subjectId);

  const eligibleChecks = useMemo(
    () => getEligibleChecksForSubject(eligibleData, subjectId),
    [eligibleData, subjectId]
  );
  const enrollments = detail?.enrollments ?? [];

  function handleActionComplete() {
    void refreshAfterAction();
  }

  return (
    <Layout
      layout="narrow"
      header={<AppHeader />}
      navigation={<AppNavigation />}
      footer={<AppFooterLinks />}
    >
      <Container layout="full-width">
        {isLoading && <Loader overlay background="background-base" />}

        {error && (
          <SectionMessage
            type="danger"
            testID="monitoring-detail-error"
            sectionTitle={t("Unable to load monitoring details")}
          >
            {t("Please try again later.")}
          </SectionMessage>
        )}

        {detail && (
          <Stack gap="space-4">
            <PersonProfileTitleCard
              name={parseName({
                first_name: detail.latest_first_name,
                last_name: detail.latest_last_name,
              })}
              email={detail.latest_email ?? undefined}
              breadcrumbs={[
                {
                  navigate: () => navigate(MONITORING_PATH),
                  title: t("Monitoring"),
                },
                {
                  title: getApplicantDisplayName(detail),
                },
              ]}
              actions={
                subjectId ? (
                  <MonitoringApplicantActionsDropdown
                    subjectId={subjectId}
                    firstName={detail.latest_first_name}
                    monitorStatusRollup={detail.monitor_status_rollup}
                    onActionComplete={handleActionComplete}
                  />
                ) : null
              }
            />

            <Tabs
              selectedKey={tab}
              onSelectionChange={(key) => setTab(String(key))}
              aria-label={t("Monitoring detail tabs")}
              testID="monitoring-detail-tabs"
            >
              <TabList aria-label={t("Monitoring detail tabs")}>
                <Tab id="checks" testID="monitoring-checks-tab">
                  {t("Checks monitored")}
                </Tab>
                <Tab id="activity" testID="monitoring-activity-tab">
                  {t("Activity log")}
                </Tab>
              </TabList>
              <TabPanel id="checks">
                <ChecksMonitoredTab
                  enrollments={enrollments}
                  eligibleChecks={[...eligibleChecks]}
                  canEnroll={canEnroll}
                  onActionComplete={handleActionComplete}
                />
              </TabPanel>
              <TabPanel id="activity">
                <ActivityLogTab subjectId={subjectId} />
              </TabPanel>
            </Tabs>
          </Stack>
        )}
      </Container>
    </Layout>
  );
}
